import { createTheme } from '@mui/material/styles';

// One flat, quiet surface system. No gradients, blurs, or glows anywhere:
// they were the main source of scroll jank and visual noise.
export const tokens = {
  bg: '#0B0E13',
  paper: '#12161D',
  raised: '#181D26',
  line: 'rgba(255,255,255,0.08)',
  lineStrong: 'rgba(255,255,255,0.16)',
  text: '#EDF1F5',
  textMuted: 'rgba(237,241,245,0.62)',
  textFaint: 'rgba(237,241,245,0.42)',
  mint: '#35F0C2',
  mintDark: '#1FCFA3',
  mintSoft: 'rgba(53,240,194,0.12)',
  mintLine: 'rgba(53,240,194,0.45)',
  onMint: '#04110D',
  danger: '#FF6B7A',
  warn: '#FFC266',
  info: '#7EB6FF',
  success: '#4ADE80',
  radius: 10,
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: tokens.mint, dark: tokens.mintDark, light: '#8CF7DC', contrastText: tokens.onMint },
    secondary: { main: tokens.info, contrastText: '#06101C' },
    error: { main: tokens.danger, contrastText: '#1A0508' },
    warning: { main: tokens.warn, contrastText: '#1A1000' },
    info: { main: tokens.info, contrastText: '#06101C' },
    success: { main: tokens.success, contrastText: '#03140A' },
    background: { default: tokens.bg, paper: tokens.paper },
    text: { primary: tokens.text, secondary: tokens.textMuted, disabled: tokens.textFaint },
    divider: tokens.line,
    action: {
      hover: 'rgba(255,255,255,0.05)',
      selected: tokens.mintSoft,
      disabledBackground: 'rgba(255,255,255,0.06)',
      disabled: tokens.textFaint,
      focus: 'rgba(53,240,194,0.2)',
    },
  },
  shape: { borderRadius: tokens.radius },
  spacing: 8,
  typography: {
    fontFamily: ['Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'Arial', 'sans-serif'].join(','),
    fontSize: 14,
    h1: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 },
    h2: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
    h3: { fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.2 },
    h4: { fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 },
    h5: { fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25, fontSize: '1.25rem' },
    h6: { fontWeight: 600, letterSpacing: 0, lineHeight: 1.3, fontSize: '1.05rem' },
    subtitle1: { fontWeight: 600, lineHeight: 1.4 },
    subtitle2: { fontWeight: 600, lineHeight: 1.4 },
    body1: { lineHeight: 1.55 },
    body2: { lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    caption: { lineHeight: 1.4 },
    overline: { letterSpacing: '0.06em', fontWeight: 600, textTransform: 'uppercase' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg,
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', borderRadius: tokens.radius },
        outlined: { borderColor: tokens.line },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.paper,
          border: `1px solid ${tokens.line}`,
          borderRadius: tokens.radius,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none', borderRadius: 0 } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          minHeight: 40,
          paddingLeft: 16,
          paddingRight: 16,
          fontSize: '0.9rem',
        },
        sizeSmall: { minHeight: 32, paddingLeft: 12, paddingRight: 12, fontSize: '0.82rem' },
        sizeLarge: { minHeight: 48, paddingLeft: 22, paddingRight: 22, fontSize: '1rem' },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': { backgroundColor: tokens.mintDark, boxShadow: 'none' },
        },
        outlined: {
          borderColor: tokens.lineStrong,
          color: tokens.text,
          '&:hover': { borderColor: tokens.mintLine, backgroundColor: tokens.mintSoft },
        },
        text: { color: tokens.text },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6, height: 26, backgroundColor: 'rgba(255,255,255,0.06)' },
        sizeSmall: { height: 22, fontSize: '0.72rem' },
        label: { paddingLeft: 8, paddingRight: 8 },
        outlined: { borderColor: tokens.lineStrong, backgroundColor: 'transparent' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.paper,
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.line },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.lineStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: tokens.mint, borderWidth: 1 },
        },
        input: { paddingTop: 11, paddingBottom: 11 },
        inputSizeSmall: { paddingTop: 8, paddingBottom: 8 },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: tokens.textMuted } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: tokens.raised, border: `1px solid ${tokens.lineStrong}`, color: tokens.text, fontSize: '0.78rem' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: tokens.raised, border: `1px solid ${tokens.lineStrong}`, marginTop: 6 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: tokens.paper, border: `1px solid ${tokens.lineStrong}`, borderRadius: 14 },
      },
    },
    MuiSkeleton: {
      styleOverrides: { root: { backgroundColor: 'rgba(255,255,255,0.06)' } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 999 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: tokens.radius, alignItems: 'center' },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: tokens.line } } },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', '&:before': { display: 'none' } },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { height: 2 } } },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } } },
    MuiCheckbox: { styleOverrides: { root: { borderRadius: 6 } } },
    MuiToggleButtonGroup: { styleOverrides: { root: { backgroundColor: tokens.bg } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderColor: tokens.line,
          color: tokens.textMuted,
          '&.Mui-selected': { color: tokens.text, backgroundColor: tokens.mintSoft, borderColor: tokens.mintLine },
        },
      },
    },
  },
});
